/**
 * Token storage. Plain module functions so the API client can use them
 * without pulling in React. The auth context in lib/auth.tsx is layered
 * on top for components.
 *
 * Tokens live in localStorage. This is a pragmatic choice for an
 * internal tool. If the threat model tightens (XSS exposure to public
 * content) we'd move to httpOnly cookies + same-site protection later.
 */
const ACCESS_KEY = 'kg.accessToken';
const REFRESH_KEY = 'kg.refreshToken';
const USER_KEY = 'kg.user';

import type { UserPublic } from './types';

export type Tokens = { accessToken: string; refreshToken: string };

const listeners = new Set<() => void>();

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) fn();
}

export function getTokens(): Tokens | null {
  if (typeof window === 'undefined') return null;
  const accessToken = window.localStorage.getItem(ACCESS_KEY);
  const refreshToken = window.localStorage.getItem(REFRESH_KEY);
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

export function setTokens(tokens: Tokens): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ACCESS_KEY, tokens.accessToken);
  window.localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  notify();
}

export function clearTokens(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(ACCESS_KEY);
  window.localStorage.removeItem(REFRESH_KEY);
  window.localStorage.removeItem(USER_KEY);
  notify();
}

export function getUser(): UserPublic | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserPublic;
  } catch {
    return null;
  }
}

export function setUser(user: UserPublic): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
  notify();
}
